/**
 * Hand a blob to the browser and release its temporary download resources.
 *
 * @param blob - the file contents
 * @param fileName - the name under which to save
 */
export function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);

    try {
        anchor.click();
    } finally {
        anchor.remove();
        URL.revokeObjectURL(url);
    }
}

/**
 * Downloads a file from a URL by fetching it as a blob and triggering a browser download
 * via a temporary anchor element. This is a web-only utility because it depends on DOM APIs
 * (`document.createElement`, `URL.createObjectURL`).
 *
 * @param url - the source URL to fetch the file from
 * @param filename - the filename to use for the downloaded file
 *
 * @throws Error if the fetch fails or the response is not ok
 * @returns a promise that resolves when the download has been triggered
 *
 * @example
 * ```ts
 * await downloadFile('https://example.com/report.pdf', 'report.pdf');
 * ```
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error('Failed to download file.');
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
}
